// Generic Mapbox component wrapper

import mapboxgl, { AnyLayer } from "mapbox-gl";
import { useRef, useEffect, useState } from "react";
import { GetMapboxStyleForBaseMap, BaseMap } from "../../BaseMapPanel";
import { DefaultMapView } from "../../Projects/ProjectOps";
import { GeoJSON, MultiPolygon } from 'geojson';
import * as turf from '@turf/turf'
import { GetGeojsonExtentBBox } from "../../GisOps";


const MAPBOX_LAYER_SOURCE_NAME: string = 'map-component-source';
const MAPBOX_LAYER_LAYER_NAME: string = 'map-component-layer';

//-------------------------------------------------------------------------------
// Component props
//-------------------------------------------------------------------------------
export interface MapboxComponentProps
{
  geom: MultiPolygon | null;
}


//-------------------------------------------------------------------------------
// Mapbox component
//-------------------------------------------------------------------------------
const MapboxComponent = (props: MapboxComponentProps) => 
{
  const mapContainer = useRef<HTMLDivElement>(null);

  const [map, setMap] = useState<mapboxgl.Map | null>(null);


  useEffect(() => 
  {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN!;

    const defaultMapView = DefaultMapView();
    
    if (mapContainer.current) 
    {
      const map = new mapboxgl.Map({
        container: mapContainer.current,
        style: GetMapboxStyleForBaseMap(BaseMap.SatelliteStreets),
        center: [defaultMapView.lng, defaultMapView.lat],
        zoom: 2.2,
        boxZoom: false,
        //minZoom: 1.3,
        //maxZoom: 15,
      });

      //map.on('style.load', OnMapboxLoaded);
      map.on('load', () => OnMapboxLoaded(map));

      setMap(map);

      // Clean up on unmount
      return () => map.remove();
    }
  }, []);

  useEffect(() => 
  {
    OnMapboxLoaded(map);
  }, [props.geom]);

  return (

    <div ref={mapContainer} style={{ width: '100%', height: '100%' }}/>

  );

  //-------------------------------------------------------------------------------
  // Called by Mapbox after it is done loading and ready for operations.
  //-------------------------------------------------------------------------------
  function OnMapboxLoaded(map: mapboxgl.Map | null)
  {
    if(!map || !props.geom) return;

    // Remove source+layers (in case they already exist from a previous update)

    if(map.getLayer(`${MAPBOX_LAYER_LAYER_NAME}-1`))
      map.removeLayer(`${MAPBOX_LAYER_LAYER_NAME}-1`);
    if(map.getLayer(`${MAPBOX_LAYER_LAYER_NAME}-2`))
      map.removeLayer(`${MAPBOX_LAYER_LAYER_NAME}-2`);
    if(map.getSource(MAPBOX_LAYER_SOURCE_NAME))
      map.removeSource(MAPBOX_LAYER_SOURCE_NAME);

    // Add the mapbox source

    map.addSource(MAPBOX_LAYER_SOURCE_NAME,
    {
      type: 'geojson',
      data: props.geom
    })

    // Add mapbox layer 1 (a black halo)

    const newMapboxLayer1: mapboxgl.Layer = 
    {
      'id': `${MAPBOX_LAYER_LAYER_NAME}-1`,
      'source': MAPBOX_LAYER_SOURCE_NAME,
      'type': 'line',
      'paint': 
      {
        'line-color': '#000000',
        'line-width': 10,
        'line-blur': 2,
        'line-opacity': 1,
      }
    };

    map.addLayer(newMapboxLayer1 as AnyLayer);

    // Add mapbox layer 2 (a thinner RED main polygon)

    const newMapboxLayer2: mapboxgl.Layer = 
    {
      'id': `${MAPBOX_LAYER_LAYER_NAME}-2`,
      'source': MAPBOX_LAYER_SOURCE_NAME,
      'type': 'line',
      'paint': 
      {
        'line-color': '#D05F61',//'rgba(1,0,0,1)',
        'line-width': 3,
        //'line-blur': 0,
        //'line-gap-width': 2,
        'line-opacity': 1,
      }
    }  

    map.addLayer(newMapboxLayer2 as AnyLayer);

    // Zoom to extent
    ZoomMapToGeojsonExtent(map, props.geom);
  }

  //-------------------------------------------------------------------------------
  // Fit the view bounds of the map to the extent of the specified geojson.
  //-------------------------------------------------------------------------------
  function ZoomMapToGeojsonExtent(map: mapboxgl.Map | null, geom: GeoJSON)
  {
    if(!map) return;

    // Make sure the geom is not empty
    if(!geom || (Object.keys(geom).length === 0 && geom.constructor === Object))
    return undefined;

    const enlargedAoiExtent = turf.transformScale(geom as any, 1.1);  // zoom out slightly more than the actual extent, looks nicer
    const bbox = GetGeojsonExtentBBox(enlargedAoiExtent); // It should already be in WSG84/EPSG:4326 (lat/long)

    // Navigate the map to the AOI area | SW-LNG, SW-LAT, NE-LNG, NE-LAT
    map.fitBounds([bbox[2], bbox[3], bbox[0], bbox[1]], {padding: 0});
  }
};


export default MapboxComponent;

